import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Spin, Tag, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { type WorkflowTemplate, type WorkflowVersion, workflowsApi } from '@/api/workflows';
import { type FieldValue } from '@/form-renderer/FieldRenderer';
import { FormShell } from '@/form-renderer/FormShell';
import { handleApiError } from '@/lib/handleApiError';

import { DevicePreviewFrame, DeviceViewportSwitcher, type DeviceKind } from './DevicePreviewFrame';

/**
 * Optional sample-values fixture for the previewed workflow. Lives under
 * `public/sample-values.json` next to the dev server — the seed script writes
 * this file when it runs so the originator can hit Preview and immediately
 * see the Application Review with realistic data, instead of typing every
 * field by hand. Production builds simply don't ship this file (404 → null).
 */
type SampleValuesFixture = {
  templateId: string;
  values: Record<string, FieldValue>;
};

const { Title, Text } = Typography;

/**
 * Originator-side preview of a workflow's DynamicForm steps. Uses the same
 * <FormShell> + <FormRenderer> that the eventual Advisor route will use, so
 * what the originator sees in Preview is exactly what the advisor will see
 * once the Advisor experience ships.
 *
 * No API actions — values live in component state and are lost on refresh
 * (intentional: Preview is for shape-checking, not data persistence).
 * Navigation between steps is free (mode='preview'); the rail shows a blue
 * check on steps whose required fields are all filled.
 */
export function PreviewPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: templateId } = useParams<{ id: string }>();
  // Optional ?stepId=… so the Approver review can jump straight to a specific
  // step's preview. Falls back to the workflow's first step.
  // Optional ?version=N pins the preview to a specific (archived or pending)
  // version's frozen step list — without it the preview reflects the
  // template's CURRENT state, which would leak v2's edits into v1's
  // preview after a new version is published.
  const [searchParams] = useSearchParams();
  const initialStepId = searchParams.get('stepId') ?? undefined;
  const versionParam = searchParams.get('version');
  const requestedVersion = versionParam !== null ? Number(versionParam) : null;
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [pinnedVersion, setPinnedVersion] = useState<WorkflowVersion | null>(null);
  const [sampleValues, setSampleValues] = useState<Record<string, FieldValue> | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  // Set when a specific ?version=N was requested but can't be resolved to a
  // frozen snapshot (unknown number, or a non-numeric param). We surface an
  // explicit error instead of silently rendering the template's CURRENT state,
  // which would masquerade as the requested version.
  const [versionNotFound, setVersionNotFound] = useState(false);
  // Device form-factor for the preview frame. <FormShell> reflows itself once
  // the frame gets narrow (rail collapses), so this only has to drive width.
  const [device, setDevice] = useState<DeviceKind>('desktop');
  const language = i18n.language.startsWith('fr') ? 'fr' : 'en';

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    setLoading(true);
    setVersionNotFound(false);
    (async () => {
      try {
        // A specific version was requested via ?version=N. A non-numeric param
        // (Number(...) → NaN) can never match a row, so treat it as not-found
        // without even hitting the versions endpoint.
        const versionRequested = requestedVersion !== null;
        const versionResolvable = versionRequested && Number.isFinite(requestedVersion);
        // Fetch the workflow + the optional sample-values fixture in parallel.
        // The fixture is just a static JSON file the seed script drops into
        // `public/`, so a missing/stale file is non-fatal: we silently ignore.
        // When a specific version is requested, also pull the versions list
        // so we can swap its frozen step list onto the template object before
        // handing it to <FormShell>. Everything else (name / description /
        // type) stays on the template — only the steps shape needs the
        // version-frozen snapshot.
        // The pinned version is fetched directly rather than picked out of a list: the
        // versions list no longer carries step snapshots, and the snapshot is the only
        // reason this page wants a version at all. A miss resolves to null, which is
        // already the not-found path below.
        const [fresh, fixtureValues, pinned] = await Promise.all([
          workflowsApi.get(templateId),
          fetchSampleValues(templateId),
          versionResolvable
            ? workflowsApi.getVersion(templateId, requestedVersion).catch(() => null)
            : Promise.resolve<WorkflowVersion | null>(null),
        ]);
        if (cancelled) return;
        if (versionRequested) {
          // Requested a version we can't resolve — show the error rather than
          // falling back to the workflow's current state.
          if (!pinned) {
            setVersionNotFound(true);
            return;
          }
          setTemplate({ ...fresh, steps: pinned.steps });
          setPinnedVersion(pinned);
        } else {
          setTemplate(fresh);
          setPinnedVersion(null);
        }
        setSampleValues(fixtureValues ?? undefined);
      } catch (err) {
        if (!cancelled) handleApiError(err, t, t('preview.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId, t, requestedVersion]);

  // Resolve which preview badge to show — Approved-not-yet-published /
  // Published / Rejected / Pending / Draft. Runs on every render but is
  // cheap; kept memoised so the render below stays declarative and the
  // early returns below can still short-circuit safely.
  const previewState = useMemo(
    () => resolvePreviewState(pinnedVersion, template),
    [pinnedVersion, template],
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (versionNotFound) {
    return (
      <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Text type="secondary">{t('preview.versionNotFound')}</Text>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          {t('preview.back')}
        </Button>
      </div>
    );
  }
  if (!template) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="secondary">{t('preview.notFound')}</Text>
      </div>
    );
  }

  const workflowTitle = language === 'fr' && template.nameFr ? template.nameFr : template.nameEn;

  return (
    <div
      style={{
        // Horizontal padding lives on <AppShell> (24 vertical / 32 horizontal).
        // Adding more here would double-pad the page and make the form's
        // left / right margins feel bigger than every other route.
        paddingBottom: 24,
        background: '#f6f7fb',
        minHeight: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 0,
            flexWrap: 'wrap',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            {workflowTitle}
          </Title>
          {previewState && (
            <Tag
              color={previewState.color}
              style={{
                marginInlineEnd: 0,
                fontWeight: 600,
                letterSpacing: 0.2,
              }}
            >
              {t(previewState.labelKey, { defaultValue: previewState.fallback })}
            </Tag>
          )}
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t('preview.modeBadge')}
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <DeviceViewportSwitcher device={device} onChange={setDevice} />
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {t('preview.back')}
          </Button>
        </div>
      </div>
      <DevicePreviewFrame device={device}>
        <FormShell
          template={template}
          language={language}
          mode="preview"
          initialStepId={initialStepId}
          initialValues={sampleValues}
          onSaveAndExit={() => navigate(-1)}
        />
      </DevicePreviewFrame>
    </div>
  );
}

/**
 * Compute the state chip shown next to the workflow title on the preview
 * header. Only fires when a specific `?version=N` is pinned — without a
 * version param the preview shows the template's live state and the chip
 * is suppressed (the small "no data is saved" note still shows).
 *
 * Rules (highest precedence first):
 *
 *   • Approved + template.currentVersionNumber === this version +
 *     template.isActive === false  → "Approved — not yet published"
 *     This is AC02's target — a freshly-approved version the approver is
 *     eyeballing before flipping the publish switch.
 *
 *   • Approved + isActive === true → "Published (live)"
 *
 *   • Approved + !current → "Approved (archived)" — an older approved
 *     version that's since been superseded.
 *
 *   • Rejected → "Rejected"
 *   • Pending  → "Pending review"
 *   • Draft    → "Draft"
 *
 * Returns null when there's no pinned version — the caller renders no
 * chip in that case so the current-template preview stays unbadged.
 */
type PreviewStateChip = {
  labelKey: string;
  fallback: string;
  color: string;
};
function resolvePreviewState(
  version: WorkflowVersion | null,
  template: WorkflowTemplate | null,
): PreviewStateChip | null {
  if (!version || !template) return null;
  const isCurrent = template.currentVersionNumber === version.versionNumber;
  const isLive = isCurrent && template.isActive;
  switch (version.status) {
    case 'Approved': {
      if (isLive) {
        return {
          labelKey: 'preview.state.publishedLive',
          fallback: 'Published (live)',
          color: 'success',
        };
      }
      if (isCurrent) {
        return {
          labelKey: 'preview.state.approvedNotPublished',
          fallback: 'Approved — not yet published',
          color: 'warning',
        };
      }
      return {
        labelKey: 'preview.state.approvedArchived',
        fallback: 'Approved (archived)',
        color: 'default',
      };
    }
    case 'Rejected':
      return {
        labelKey: 'preview.state.rejected',
        fallback: 'Rejected',
        color: 'error',
      };
    case 'Pending':
      return {
        labelKey: 'preview.state.pending',
        fallback: 'Pending review',
        color: 'processing',
      };
    case 'Draft':
      return {
        labelKey: 'preview.state.draft',
        fallback: 'Draft',
        color: 'default',
      };
    default:
      return null;
  }
}

/**
 * Try to fetch `public/sample-values.json`. Returns null on any failure
 * (file missing, network error, malformed JSON, or the fixture is for a
 * different workflow). Production builds simply don't ship the file.
 */
async function fetchSampleValues(templateId: string): Promise<Record<string, FieldValue> | null> {
  try {
    const res = await fetch('/sample-values.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const parsed = (await res.json()) as SampleValuesFixture;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.templateId !== templateId) return null;
    return parsed.values ?? null;
  } catch {
    return null;
  }
}
